import { ReactNode, useEffect, useMemo, useState } from 'react';
import { useHeaderData } from '@screentone/addon-auth-wrapper';
import { Card as SkedCard } from '@screentone/addon-sked';
import { IconData } from '@screentone/addon-sked/dist/components/Card/types';
import { IconPackage } from '@screentone/core';
import classnames from 'classnames';

import { CommonEntityProps } from 'components/datamodel/commonEntityProps';
import { DuplicateItemType } from 'contexts/content-id-tracking/ContentIdTrackingContext';
import { useContentIdTracking } from 'contexts/content-id-tracking/useContentIdTracking';
// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import { MenuActionsKeys } from 'contexts/context-menu-actions/ContextMenuActionsContext';
// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import { useContextMenuActions } from 'contexts/context-menu-actions/useContextMenuActions';
// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import { useDataModelContext } from 'contexts/datamodel/useDataModel';
import {
  GraphicLinkedItem as GraphicLinkedItemModel,
  LinkedItem as LinkedItemModel,
  MobileLinkedItem as MobileLinkedItemModel
} from 'data/generated/graphql';
// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import useIsBlockedAgency from 'utils/hooks/useIsBlockedAgency';
import { isGraphicLinkedItem, isMobileLinkedItem } from 'utils/mobile-content';
import { resizeIMImage } from 'utils/url';
import { getLinkedItemTitle } from './LinkedItem.constants';
import styles from './LinkedItem.module.scss';
// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import { LinkedItemForm } from './LinkedItemForm';
import { CommonContentProps } from '../commonContentProps';
import { CONTENT_CARD_TEST_IDS } from '../skedCardTestIds';

const IM_WIDTH = {
  THUMBNAIL: 40,
  ENLARGED: 180
};

interface GraphicRelatedLink {
  type?: string;
  properties?: { location?: string };
  display_variants?: Record<string, { url?: string } | string>;
}

interface GraphicJson {
  alt?: {
    capi?: {
      links?: {
        related?: GraphicRelatedLink[];
      };
    };
  };
}
interface LinkedItemsProps extends CommonEntityProps, CommonContentProps {
  data: LinkedItemModel | MobileLinkedItemModel | GraphicLinkedItemModel;
}

export const LinkedItem = ({
  data: linkedItem,
  parentHierarchyId,
  index,
  isDraft,
  isHistory,
  isReadOnly
}: LinkedItemsProps) => {
  const { state } = useHeaderData();
  const { generateHierarchyId, isEditingLinkedItem } = useDataModelContext();
  const { renderActions } = useContextMenuActions();
  const { trackContentIds, ignoreContentIds, getDuplicateClassName } = useContentIdTracking();
  const hierarchyId = generateHierarchyId(linkedItem, parentHierarchyId, index);
  const actionsType = isHistory ? MenuActionsKeys.HistoryLinkedItem : MenuActionsKeys.DraftLinkedItem;

  useEffect(() => {
    if (isDraft) {
      trackContentIds(hierarchyId, linkedItem.attributes.id);
    }

    return () => {
      ignoreContentIds(hierarchyId, linkedItem.attributes.id);
    };
  }, [hierarchyId, ignoreContentIds, isDraft, linkedItem.attributes.id, trackContentIds]);

  const duplicateClassName = isDraft
    ? getDuplicateClassName(
        state.headerConfig.currentTheme ?? 'light',
        DuplicateItemType.CARD,
        linkedItem.attributes.id
      )
    : '';
  const isBlockedAgency = useIsBlockedAgency();
  const isBlockedImage = useMemo(
    () => isBlockedAgency(linkedItem.attributes.image?.credit),
    [linkedItem.attributes.image?.credit, isBlockedAgency]
  );

  const isBlockedImageClassName = isDraft && isBlockedImage ? styles.blockedAgencyHighlight : '';
  const className = classnames(duplicateClassName, isBlockedImageClassName);

  const [graphicImageUrl, setGraphicImageUrl] = useState<string>('');
  const graphicJsonUrl = isGraphicLinkedItem(linkedItem) ? (linkedItem.attributes.hosted_url ?? '') : '';

  useEffect(() => {
    let cancelled = false;
    setGraphicImageUrl('');

    if (graphicJsonUrl) {
      void fetch(graphicJsonUrl)
        .then((res) => res.json() as Promise<GraphicJson>)
        .then((json) => {
          if (cancelled) return;
          const related = json.alt?.capi?.links?.related ?? [];
          const imageEntry = related.find((r) => r.type === 'image');
          let location: string | undefined;
          if (imageEntry?.display_variants) {
            const defaultKey = imageEntry.display_variants.default;
            if (typeof defaultKey === 'string') {
              const variant = imageEntry.display_variants[defaultKey];
              if (variant && typeof variant === 'object') location = variant.url;
            }
          }
          location ??= related[0]?.properties?.location;
          setGraphicImageUrl(location ?? '');
        })
        .catch(() => undefined);
    }

    return () => {
      cancelled = true;
    };
  }, [graphicJsonUrl]);

  const image = isGraphicLinkedItem(linkedItem)
    ? graphicImageUrl
    : linkedItem.attributes.image?.name
      ? linkedItem.attributes.image.name
      : '';
  const imageUrlThumbnail = useMemo(() => resizeIMImage(image, { width: IM_WIDTH.THUMBNAIL, size: 1 }), [image]) ?? '';
  const imageUrlEnlarged = useMemo(() => resizeIMImage(image, { width: IM_WIDTH.ENLARGED }), [image]) ?? '';

  if (isEditingLinkedItem.has(linkedItem.uuid)) {
    if (isGraphicLinkedItem(linkedItem)) {
      return (
        <LinkedItemForm
          defaultHeadline={linkedItem.attributes.title ?? ''}
          defaultMedia={null}
          defaultSummary={linkedItem.attributes.description}
          defaultUrl={linkedItem.attributes.hosted_url ?? ''}
          defaultValidationBypassed={false}
          defaultCtaLink={linkedItem.attributes.ctaLink ?? ''}
          defaultCtaText={linkedItem.attributes.ctaText ?? ''}
          hierarchyId={hierarchyId}
          uuid={linkedItem.uuid}
          isGraphicLinkedItem
          isReadOnly={isReadOnly}
        />
      );
    }
    return (
      <LinkedItemForm
        defaultHeadline={linkedItem.attributes.title ?? ''}
        defaultMedia={linkedItem.attributes.image}
        defaultSummary={linkedItem.attributes.description}
        defaultUrl={linkedItem.attributes.hosted_url ?? ''}
        defaultValidationBypassed={false}
        hierarchyId={hierarchyId}
        uuid={linkedItem.uuid}
        isMobileLinkedItem={isMobileLinkedItem(linkedItem)}
        isReadOnly={isReadOnly}
      />
    );
  }

  const slugTokens: IconData[] = [
    {
      icon: IconPackage as unknown as ReactNode,
      tooltip: isGraphicLinkedItem(linkedItem) ? 'Graphic Linked Item' : 'Linked Item',
      tooltipPosition: 'right'
    }
  ];
  const headline = getLinkedItemTitle(linkedItem.attributes.title, linkedItem.attributes.id);
  return (
    <div data-allesseh-id={`not-in-allesseh::${linkedItem.attributes.id}`} data-model-hierarchy-id={hierarchyId}>
      <SkedCard className={className} testIds={CONTENT_CARD_TEST_IDS}>
        <SkedCard.Header
          menu={renderActions(actionsType, { hierarchyId, entity: linkedItem, index })}
          slugTokens={slugTokens}
          testIds={CONTENT_CARD_TEST_IDS.header}
        />
        <SkedCard.Content
          type="curation"
          headline={headline}
          image={imageUrlThumbnail}
          validImage={!(isDraft && isBlockedImage)}
          imageEnlarged={!(isDraft && isBlockedImage) ? imageUrlEnlarged : undefined}
          imageOverride={<div className={styles.noImage} />}
          testIds={CONTENT_CARD_TEST_IDS.content}
        />
      </SkedCard>
    </div>
  );
};
